import { Injectable } from '@angular/core';
import { LANGUAGES, NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    IManualAccount,
    IMovementScheduleAccount,
    IMovementScheduleComponent,
    IMovementScheduleEntries,
    IMovementScheduleFli,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IMovementScheduleEditableColumnHeaders } from '@modeso/types__aumico-lib-notes-be';
import { I18NextService } from 'angular-i18next';
import { cloneDeep } from 'lodash-es';
import { MovementScheduleColumnIndexes } from '../enums/movementScheduleColumnIndexes.enum';
import { IFLIForNotes } from '../interfaces/IFLIForNotes.interface';
import { IMovementScheduleFormFli } from '../interfaces/movement-schedule-form-fli.interface';
import { IMovementScheduleFormArray } from '../interfaces/movement-schedule/IMovementScheduleFormArray.interface';
import { IMovementSchedulePrintPreview } from '../interfaces/movement-schedule/IMovementSchedulePrintPreview.interface';
import {
    IMovementScheduleTable,
    IMovementScheduleTableForm,
} from '../interfaces/movement-schedule/IMovementScheduleTable.interface';

@Injectable({ providedIn: 'root' })
export class MovementScheduleHelper {
    constructor(private readonly localizedTexts: I18NextService) {}
    public getTotalByColumn(column: string, dataSource: IMovementScheduleAccount[]): number {
        const currentDataSource = [...dataSource];

        const columnCellsValue = currentDataSource.map<number | null>((data) => data[column]);
        return NumberUtils.sumIgnoringInfiniteNumbers(columnCellsValue, 0);
    }

    // number are outputted from the form as string, this method is just to convert it to be numbers and prepare headers
    public prepareMovementScheduleFormData(
        formData: IMovementScheduleTableForm,
        initialHeaders: IMovementScheduleEditableColumnHeaders | undefined,
    ): IMovementScheduleTable {
        const { header1, header2, header3, header4 } = formData.headers;
        let headers: IMovementScheduleEditableColumnHeaders;
        if (initialHeaders != null) {
            headers = {
                column1: { ...initialHeaders.column1, ...header1 },
                column2: { ...initialHeaders.column2, ...header2 },
                column3: { ...initialHeaders.column3, ...header3 },
                column4: { ...initialHeaders.column4, ...header4 },
            };
        } else {
            headers = {
                column1: { ...header1 },
                column2: { ...header2 },
                column3: { ...header3 },
                column4: { ...header4 },
            };
        }

        const preparedDataSource: IMovementScheduleAccount[] = [];

        for (const row of formData.dataSource) {
            let column1: number | null = null;
            if (row.column1 != null && NumberUtils.isFinite(row.column1)) {
                column1 = NumberUtils.roundNumber(row.column1);
            }

            let column2: number | null = null;
            if (row.column2 != null && NumberUtils.isFinite(row.column2)) {
                column2 = NumberUtils.roundNumber(row.column2);
            }

            let column3: number | null = null;
            if (row.column3 != null && NumberUtils.isFinite(row.column3)) {
                column3 = NumberUtils.roundNumber(row.column3);
            }

            let column4: number | null = null;
            if (row.column4 != null && NumberUtils.isFinite(row.column4)) {
                column4 = NumberUtils.roundNumber(row.column4);
            }
            const currentRow: IMovementScheduleAccount = {
                ...row,
                column1,
                column2,
                column3,
                column4,
            };
            preparedDataSource.push(currentRow);
        }
        const preparedFormData: IMovementScheduleTable = {
            headers,
            dataSource: preparedDataSource,
            totalRow: { ...formData.totalRow },
        };

        preparedFormData.dataSource = [...preparedDataSource];
        return preparedFormData;
    }

    public prepareMovementScheduleFlis(flis: IFLIForNotes[]): IMovementScheduleFli[] {
        const movementScheduleFlis: IMovementScheduleFli[] = [];
        const generalFlis: IFLIForNotes[] = cloneDeep(flis);

        for (const fli of generalFlis) {
            const currentMovementScheduleFli: IMovementScheduleFli & { itemType: ItemType } = {
                id: fli.id,
                name: fli.name,
                caption: fli.caption != null ? { 'de-ch': fli.caption, 'en-us': fli.caption } : { 'de-ch': fli.name },
                headers: this.getInitialHeaderOptions(),
                position: fli.position,
                totalRow: undefined,
                accounts: this.prepareMovementScheduleAccounts(fli.accounts),
                templateType: fli.belongingTemplateType,
                itemType: fli.itemType,
            };

            movementScheduleFlis.push(currentMovementScheduleFli);
        }

        return movementScheduleFlis;
    }

    private prepareMovementScheduleAccounts(accounts: IManualAccount[]): IMovementScheduleAccount[] {
        const generalAccounts: IManualAccount[] = [...accounts];
        const movementScheduleAccounts: IMovementScheduleAccount[] = [];

        for (const account of generalAccounts) {
            const currentMovementSchedule: IMovementScheduleAccount = {
                id: account.id.toString(),
                text: account.accountName,
                closeValue: NumberUtils.roundNumber(account.currentValue),
                openValue: NumberUtils.roundNumber(account.previousValue),
                column1: null,
                column2: null,
                column3: null,
                column4: null,
            };

            movementScheduleAccounts.push(currentMovementSchedule);
        }

        return movementScheduleAccounts;
    }

    public getMovementScheduleFlisFromFormArray(formArrayValue: IMovementScheduleFormArray[]): IMovementScheduleFli[] {
        const movementScheduleFlis: IMovementScheduleFli[] = [];
        for (const value of formArrayValue) {
            movementScheduleFlis.push(value.fli);
        }

        return movementScheduleFlis;
    }

    public addHeadersToMovementScheduleFlis(
        headerOptions: IMovementScheduleEditableColumnHeaders,
        flis: IMovementScheduleFli[],
    ): IMovementScheduleFli[] {
        if (headerOptions == null || flis == null) {
            return [];
        }

        const movementScheduleFlis: IMovementScheduleFli[] = [];

        for (const fli of flis) {
            if (fli.headers == null) {
                fli.headers = headerOptions;
            }
            movementScheduleFlis.push(fli);
        }

        return movementScheduleFlis;
    }

    public prioritizeFlis(flis: IMovementScheduleFli[]): IMovementScheduleFli[] {
        const nonPrioritizedFlis: IMovementScheduleFli[] = [...flis];
        return nonPrioritizedFlis.sort((fli1, fli2) => (fli1.position < fli2.position ? -1 : 1));
    }

    public addTranslatedCaptionToMovementScheduleFlis(
        flis: IMovementScheduleFli[],
        language: string,
        defaultLanguage: string,
    ): IMovementScheduleFormFli[] {
        if (flis == null) {
            return [];
        }

        const movementScheduleTranslatedCaptionFlis: IMovementScheduleFormFli[] = [];

        for (const fli of flis) {
            if (fli == null || fli.caption == null) {
                movementScheduleTranslatedCaptionFlis.push(fli as IMovementScheduleFormFli);
            } else {
                const movementScheduleTranslatedCaptionFli: IMovementScheduleFormFli = {
                    ...fli,
                    translatedCaption: fli.caption[language] || fli.caption[defaultLanguage],
                };

                movementScheduleTranslatedCaptionFlis.push(movementScheduleTranslatedCaptionFli);
            }
        }

        return movementScheduleTranslatedCaptionFlis;
    }

    public getMovementSchedulePrintPreviewData(
        currentMovementScheduleFlis: IMovementScheduleFli[],
        previousMovementScheduleComponentData: IMovementScheduleComponent,
    ): IMovementSchedulePrintPreview {
        const previousFlisRelatedToTheCurrent = new Map<number, IMovementScheduleFli>();

        const preparedCurrentFliForPrintPreview = this.getRoundedFlis(currentMovementScheduleFlis);

        if (previousMovementScheduleComponentData != null) {
            const previousFlis = this.getRoundedFlis(previousMovementScheduleComponentData.flis);

            preparedCurrentFliForPrintPreview.forEach((currentFli, index) => {
                const autofillOfFli = previousFlis.find((previousFli) => previousFli.id === currentFli.id);

                if (autofillOfFli != null) {
                    previousFlisRelatedToTheCurrent.set(index, autofillOfFli);
                }
            });
        }

        return {
            currentMovementScheduleFlis: preparedCurrentFliForPrintPreview,
            previousFlisOfCurrentFlis: previousFlisRelatedToTheCurrent,
        };
    }

    private getRoundedFlis(flis: IMovementScheduleFli[]): IMovementScheduleFli[] {
        const movementScheduleFlis: IMovementScheduleFli[] = [];
        for (const fli of flis) {
            const currentFli = cloneDeep<IMovementScheduleFli>(fli);
            currentFli.accounts = this.getRoundedAccounts(currentFli.accounts);
            currentFli.totalRow = this.calculateTotalOfMovementScheduleFli(fli);
            movementScheduleFlis.push(currentFli);
        }
        return movementScheduleFlis;
    }

    private getRoundedAccounts(accounts: IMovementScheduleAccount[]): IMovementScheduleAccount[] {
        const movementsScheduleAccounts: IMovementScheduleAccount[] = [];
        for (const account of accounts) {
            let column1: number | null = null;
            if (account.column1 != null && NumberUtils.isFinite(account.column1)) {
                column1 = NumberUtils.roundNumber(account.column1);
            }

            let column2: number | null = null;
            if (account.column2 != null && NumberUtils.isFinite(account.column2)) {
                column2 = NumberUtils.roundNumber(account.column2);
            }

            let column3: number | null = null;
            if (account.column3 != null && NumberUtils.isFinite(account.column3)) {
                column3 = NumberUtils.roundNumber(account.column3);
            }

            let column4: number | null = null;
            if (account.column4 != null && NumberUtils.isFinite(account.column4)) {
                column4 = NumberUtils.roundNumber(account.column4);
            }

            const movementsScheduleAccount: IMovementScheduleAccount = {
                id: account.id,
                text: account.text,
                closeValue: NumberUtils.roundNumber(account.closeValue),
                column1,
                column2,
                column3,
                column4,
                openValue: NumberUtils.roundNumber(account.openValue),
            };
            movementsScheduleAccounts.push(movementsScheduleAccount);
        }
        return movementsScheduleAccounts;
    }

    public calculateTotalOfMovementScheduleFli(fli: IMovementScheduleFli): IMovementScheduleEntries {
        return {
            text: fli.totalRow.text,
            closeValue: this.getTotalByColumn(MovementScheduleColumnIndexes.CLOSE_VALUE, fli.accounts),
            column1: this.getTotalByColumn(MovementScheduleColumnIndexes.FIRST_COLUMN, fli.accounts),
            column2: this.getTotalByColumn(MovementScheduleColumnIndexes.SECOND_COLUMN, fli.accounts),
            column3: this.getTotalByColumn(MovementScheduleColumnIndexes.THIRD_COLUMN, fli.accounts),
            column4: this.getTotalByColumn(MovementScheduleColumnIndexes.FORTH_COLUMN, fli.accounts),
            openValue: this.getTotalByColumn(MovementScheduleColumnIndexes.OPEN_VALUE, fli.accounts),
        };
    }

    public createEmptyMovementScheduleFli(): IMovementScheduleFli {
        return {
            id: '',
            name: '',
            position: NaN,
            headers: this.getInitialHeaderOptions(),
            accounts: [],
            totalRow: {} as IMovementScheduleEntries,
            templateType: null,
        };
    }

    public getInitialHeaderOptions(): IMovementScheduleEditableColumnHeaders {
        const column1Header = {};
        const column2Header = {};
        const column3Header = {};
        const column4Header = {};

        Object.values(LANGUAGES).forEach((language) => {
            column1Header[language.toLocaleLowerCase()] = this.localizedTexts.t(
                'aumico_notes_movement_schedule_component_column1_header',
                {
                    lng: language,
                },
            ) as string;
            column2Header[language.toLocaleLowerCase()] = this.localizedTexts.t(
                'aumico_notes_movement_schedule_component_column2_header',
                {
                    lng: language,
                },
            ) as string;
            column3Header[language.toLocaleLowerCase()] = this.localizedTexts.t(
                'aumico_notes_movement_schedule_component_column3_header',
                {
                    lng: language,
                },
            ) as string;
            column4Header[language.toLocaleLowerCase()] = this.localizedTexts.t(
                'aumico_notes_movement_schedule_component_column4_header',
                {
                    lng: language,
                },
            ) as string;
        });

        const intialHeaderOptions: IMovementScheduleEditableColumnHeaders = {
            column1: column1Header,
            column2: column2Header,
            column3: column3Header,
            column4: column4Header,
        };

        return intialHeaderOptions;
    }
}
